import React, { useState, useEffect } from 'react';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

interface UnsettledAmountItem {
  id?: number;
  distributor_name?: string;
  distributor_id?: number;
  unsettled_amount?: number;
  total_amount?: number;
  settled_amount?: number;
  invoice_no?: string;
  invoice_date?: string;
  created_at?: string;
  [key: string]: any;
}

interface SettlementFormData {
  settlement_amount: number;
  payment_mode: string;
  payment_details: {
    upi_id: string;
    transaction_id: string;
  };
  notes: string;
}

const UnsettledAmount: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [unsettledData, setUnsettledData] = useState<UnsettledAmountItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [settlingPurchaseId, setSettlingPurchaseId] = useState<number | null>(null);
  const [settlementError, setSettlementError] = useState<string>('');
  const [settlementSuccess, setSettlementSuccess] = useState<number | null>(null);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<{id: number, unsettled: number} | null>(null);
  const [showReceiveConfirmation, setShowReceiveConfirmation] = useState(false);
  const [purchaseToReceive, setPurchaseToReceive] = useState<{id: number, distributor_name?: string, invoice_no?: string} | null>(null);
  const [receivingPurchaseId, setReceivingPurchaseId] = useState<number | null>(null);
  const [receiveError, setReceiveError] = useState<string>('');
  const [formData, setFormData] = useState<SettlementFormData>({
    settlement_amount: 0,
    payment_mode: 'upi',
    payment_details: {
      upi_id: '',
      transaction_id: ''
    },
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch unsettled amounts
  const fetchUnsettledAmounts = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        setError('Authentication token not found. Please log in again.');
        setIsLoading(false);
        return;
      }
      
      // Fetch unsettled amounts from settlement API
      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/settlement', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle different response structures
      let unsettled: any[] = [];
      if (Array.isArray(data)) {
        unsettled = data;
      } else if (data && data.items && Array.isArray(data.items)) {
        unsettled = data.items;
      } else if (data && data.purchases && Array.isArray(data.purchases)) {
        unsettled = data.purchases;
      } else if (data && data.data && Array.isArray(data.data)) {
        unsettled = data.data;
      } else if (data && data.settlements && Array.isArray(data.settlements)) {
        unsettled = data.settlements;
      }
      
      setUnsettledData(unsettled);
    } catch (error: any) {
      console.error('Error fetching unsettled amounts:', error);
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
        setError('Unable to connect to the server. Please check your connection.');
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(`Failed to load unsettled amounts: ${error.message}`);
      }
      setUnsettledData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnsettledAmounts();
  }, []);

  // Open settlement modal
  const openSettlementModal = (purchaseId: number, unsettledAmount: number) => {
    setSelectedPurchase({ id: purchaseId, unsettled: unsettledAmount });
    setFormData({
      settlement_amount: unsettledAmount,
      payment_mode: 'upi',
      payment_details: {
        upi_id: '',
        transaction_id: ''
      },
      notes: ''
    });
    setFormErrors({});
    setSettlementError('');
    setShowSettlementModal(true);
  };

  // Close settlement modal
  const closeSettlementModal = () => {
    setShowSettlementModal(false);
    setSelectedPurchase(null);
    setFormErrors({});
    setSettlementError('');
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.settlement_amount || formData.settlement_amount <= 0) {
      errors.settlement_amount = 'Settlement amount is required and must be greater than 0';
    }
    
    if (!formData.payment_mode) {
      errors.payment_mode = 'Payment mode is required';
    }
    
    if (formData.payment_mode === 'upi') {
      if (!formData.payment_details.upi_id || formData.payment_details.upi_id.trim() === '') {
        errors.upi_id = 'UPI ID is required';
      }
      if (!formData.payment_details.transaction_id || formData.payment_details.transaction_id.trim() === '') {
        errors.transaction_id = 'Transaction ID is required';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle settlement
  const handleSettle = async () => {
    if (!selectedPurchase) return;
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSettlingPurchaseId(selectedPurchase.id);
      setSettlementError('');
      setSettlementSuccess(null);
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        setSettlementError('Authentication token not found. Please log in again.');
        setSettlingPurchaseId(null);
        return;
      }
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${selectedPurchase.id}/settlement`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check if payment is fully settled
      // The settlement amount should equal the unsettled amount that was being settled
      const isFullySettled = formData.settlement_amount >= selectedPurchase.unsettled;
      
      // Close modal and show success notification
      closeSettlementModal();
      setSettlementSuccess(selectedPurchase.id);
      setShowSuccessNotification(true);
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 3000);
      
      // If payment is fully settled, show receive confirmation popup
      if (isFullySettled) {
        // Find the purchase details from unsettledData
        const purchaseDetails = unsettledData.find(item => item.id === selectedPurchase.id);
        setPurchaseToReceive({
          id: selectedPurchase.id,
          distributor_name: purchaseDetails?.distributor_name,
          invoice_no: purchaseDetails?.invoice_no
        });
        // Show popup after a short delay to let the success notification appear first
        setTimeout(() => {
          setShowReceiveConfirmation(true);
        }, 500);
      }
      
      // Refresh the unsettled amounts list after a short delay
      setTimeout(() => {
        fetchUnsettledAmounts();
        setSettlementSuccess(null);
      }, 1500);
      
    } catch (error: any) {
      console.error('Error settling purchase:', error);
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
        setSettlementError('Unable to connect to the server. Please check your connection.');
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setSettlementError('Authentication failed. Please log in again.');
      } else {
        setSettlementError(`Failed to settle purchase: ${error.message}`);
      }
    } finally {
      setSettlingPurchaseId(null);
    }
  };

  // Handle marking purchase as received
  const handleMarkAsReceived = async () => {
    if (!purchaseToReceive) return;
    
    try {
      setReceivingPurchaseId(purchaseToReceive.id);
      setReceiveError('');
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        setReceiveError('Authentication token not found. Please log in again.');
        setReceivingPurchaseId(null);
        return;
      }
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseToReceive.id}/receive`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Close the receive confirmation popup
      setShowReceiveConfirmation(false);
      setPurchaseToReceive(null);
      
      // Refresh the unsettled amounts list
      setTimeout(() => {
        fetchUnsettledAmounts();
      }, 500);
      
    } catch (error: any) {
      console.error('Error marking purchase as received:', error);
      // Enhanced CORS error handling
      if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('Access-Control-Allow-Origin') || error.message.includes('network') ||
          error.message.includes('ERR_FAILED') || error.message.includes('preflight request'))) {
        setReceiveError('CORS Error: Unable to connect to the server. Please check your connection and ensure the server allows requests from this origin.');
      } else if (error.message && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
        setReceiveError('Authentication failed. Please log in again.');
      } else {
        setReceiveError(`Failed to mark purchase as received: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setReceivingPurchaseId(null);
    }
  };

  // Calculate total unsettled amount
  const totalUnsettled = unsettledData.reduce((sum, item) => {
    const amount = item.unsettled_amount || ((item.total_amount || 0) - (item.settled_amount || 0));
    return sum + (amount > 0 ? amount : 0);
  }, 0);

  return (
    <div style={{
      width: '100%',
      padding: isMobile ? '0' : '0.25rem'
    }}>
      {/* Stats Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
          borderRadius: '10px',
          padding: '0.875rem',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: isMobile ? '1.25rem' : '1.5rem',
            fontWeight: '900',
            color: '#ef4444',
            marginBottom: '0.25rem',
            lineHeight: '1.2'
          }}>
            ₹{totalUnsettled.toFixed(2)}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#6b7280',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            lineHeight: '1.2'
          }}>
            Total Unsettled
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
          borderRadius: '10px',
          padding: '0.875rem',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: isMobile ? '1.25rem' : '1.5rem',
            fontWeight: '900',
            color: '#f59e0b',
            marginBottom: '0.25rem',
            lineHeight: '1.2'
          }}>
            {unsettledData.length}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#6b7280',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            lineHeight: '1.2'
          }}>
            Pending Purchases
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#6b7280'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #ef4444',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 0.75rem auto'
          }}></div>
          <p style={{ fontSize: '0.85rem', fontWeight: '500', margin: 0 }}>Loading unsettled amounts...</p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '10px',
          padding: '1rem',
          textAlign: 'center',
          color: '#ef4444',
          marginBottom: '1rem'
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginBottom: '0.5rem', opacity: 0.7 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <p style={{ fontSize: '0.85rem', fontWeight: '500', margin: '0 0 0.75rem 0' }}>{error}</p>
          <button
            onClick={fetchUnsettledAmounts}
            style={{
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#dc2626';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ef4444';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Unsettled Amounts List */}
      {!isLoading && !error && (
        <div className="unsettled-amount-list" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          maxHeight: isMobile ? '400px' : '500px',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '0.5rem'
        }}>
          {unsettledData.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#6b7280',
              background: 'rgba(16, 185, 129, 0.05)',
              borderRadius: '10px',
              border: '1px solid rgba(16, 185, 129, 0.1)'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                <path d="M12 21c0-1-1-3-3-3s-3 2-3 3 1 3 3 3 3-2 3-3z"/>
                <path d="M12 3c0 1-1 3-3 3S6 4 6 3 7 0 9 0s3 2 3 3z"/>
              </svg>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.375rem',
                lineHeight: '1.2'
              }}>
                All Settled!
              </h3>
              <p style={{
                fontSize: '0.85rem',
                color: '#6b7280',
                margin: 0,
                lineHeight: '1.3'
              }}>
                No unsettled amounts at this time.
              </p>
            </div>
          ) : (
            unsettledData.map((item) => {
              const unsettled = item.unsettled_amount || ((item.total_amount || 0) - (item.settled_amount || 0));
              const settled = item.settled_amount || 0;
              const total = item.total_amount || (unsettled + settled);
              
              return (
                <div
                  key={item.id || Math.random()}
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                    backdropFilter: 'blur(25px)',
                    borderRadius: '10px',
                    padding: isMobile ? '0.75rem' : '1rem',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    <div style={{ flex: '1', minWidth: '180px' }}>
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#6b7280',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.2rem',
                        lineHeight: '1.2'
                      }}>
                        {item.distributor_name || 'Distributor'}
                      </div>
                      {item.invoice_no && (
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#9ca3af',
                          fontFamily: 'monospace',
                          lineHeight: '1.2'
                        }}>
                          Invoice: {item.invoice_no}
                        </div>
                      )}
                    </div>
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444',
                      padding: '0.4rem 0.625rem',
                      borderRadius: '6px',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontWeight: '700',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.2'
                    }}>
                      ₹{unsettled.toFixed(2)}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: '#e5e7eb',
                    borderRadius: '3px',
                    overflow: 'hidden',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{
                      width: `${total > 0 ? (settled / total) * 100 : 0}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>

                  {/* Amount Details */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
                    gap: '0.5rem',
                    fontSize: '0.75rem'
                  }}>
                    <div>
                      <div style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.2rem', fontSize: '0.7rem', lineHeight: '1.2' }}>Total</div>
                      <div style={{ color: '#374151', fontWeight: '700', lineHeight: '1.2' }}>₹{total.toFixed(2)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.2rem', fontSize: '0.7rem', lineHeight: '1.2' }}>Settled</div>
                      <div style={{ color: '#10b981', fontWeight: '700', lineHeight: '1.2' }}>₹{settled.toFixed(2)}</div>
                    </div>
                    <div style={isMobile ? { gridColumn: '1 / -1' } : {}}>
                      <div style={{ color: '#6b7280', fontWeight: '600', marginBottom: '0.2rem', fontSize: '0.7rem', lineHeight: '1.2' }}>Unsettled</div>
                      <div style={{ color: '#ef4444', fontWeight: '700', lineHeight: '1.2' }}>₹{unsettled.toFixed(2)}</div>
                    </div>
                  </div>

                  <div style={{
                    marginTop: '0.5rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}>
                    {item.invoice_date && (
                      <div style={{
                        fontSize: '0.7rem',
                        color: '#9ca3af',
                        lineHeight: '1.2'
                      }}>
                        Date: {new Date(item.invoice_date).toLocaleDateString()}
                      </div>
                    )}
                    <button
                      onClick={() => openSettlementModal(item.id || 0, unsettled)}
                      disabled={settlingPurchaseId === item.id || unsettled <= 0}
                      style={{
                        padding: '0.4rem 0.75rem',
                        background: settlingPurchaseId === item.id 
                          ? '#9ca3af' 
                          : settlementSuccess === item.id
                          ? '#10b981'
                          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        cursor: settlingPurchaseId === item.id || unsettled <= 0 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        opacity: settlingPurchaseId === item.id || unsettled <= 0 ? 0.6 : 1,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        if (settlingPurchaseId !== item.id && unsettled > 0) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (settlingPurchaseId !== item.id && unsettled > 0) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {settlingPurchaseId === item.id ? (
                        <>
                          <div style={{
                            width: '12px',
                            height: '12px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            borderTop: '2px solid white',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite'
                          }}></div>
                          Settling...
                        </>
                      ) : settlementSuccess === item.id ? (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          Settled!
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                          </svg>
                          Settle
                        </>
                      )}
                    </button>
                  </div>
                  {settlementError && settlingPurchaseId === item.id && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      color: '#ef4444',
                      lineHeight: '1.3'
                    }}>
                      {settlementError}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Settlement Modal */}
      {showSettlementModal && selectedPurchase && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 0
        }}
        onClick={closeSettlementModal}
        >
          <div style={{
            background: 'white',
            borderRadius: isMobile ? '12px' : '0',
            padding: isMobile ? '1rem' : '1.25rem',
            width: '100%',
            height: '100%',
            maxWidth: isMobile ? '90%' : '100%',
            maxHeight: isMobile ? '90%' : '100%',
            overflowY: 'auto',
            boxShadow: isMobile ? '0 20px 60px rgba(0, 0, 0, 0.3)' : 'none',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeSettlementModal}
              style={{
                position: 'absolute',
                top: isMobile ? '0.75rem' : '0.75rem',
                right: isMobile ? '0.75rem' : '0.75rem',
                background: 'transparent',
                border: 'none',
                fontSize: '1.25rem',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '0.25rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              ×
            </button>

            {/* Modal Header */}
            <h2 style={{
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '0.75rem',
              paddingRight: '2rem',
              lineHeight: '1.2'
            }}>
              Settle Payment
            </h2>

            {/* Form */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.625rem',
              flex: 1,
              overflowY: 'auto'
            }}>
              {/* Settlement Amount */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.375rem',
                  lineHeight: '1.2'
                }}>
                  Settlement Amount <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.settlement_amount}
                  onChange={(e) => setFormData({ ...formData, settlement_amount: parseFloat(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.625rem',
                    border: `1.5px solid ${formErrors.settlement_amount ? '#ef4444' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = formErrors.settlement_amount ? '#ef4444' : '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                {formErrors.settlement_amount && (
                  <p style={{ color: '#ef4444', fontSize: '0.7rem', marginTop: '0.2rem', margin: 0, lineHeight: '1.2' }}>
                    {formErrors.settlement_amount}
                  </p>
                )}
              </div>

              {/* Payment Mode */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.375rem',
                  lineHeight: '1.2'
                }}>
                  Payment Mode <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={formData.payment_mode}
                  onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.625rem',
                    border: `1.5px solid ${formErrors.payment_mode ? '#ef4444' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = formErrors.payment_mode ? '#ef4444' : '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="card">Card</option>
                </select>
                {formErrors.payment_mode && (
                  <p style={{ color: '#ef4444', fontSize: '0.7rem', marginTop: '0.2rem', margin: 0, lineHeight: '1.2' }}>
                    {formErrors.payment_mode}
                  </p>
                )}
              </div>

              {/* UPI ID (if payment mode is UPI) */}
              {formData.payment_mode === 'upi' && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.375rem',
                    lineHeight: '1.2'
                  }}>
                    UPI ID <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.payment_details.upi_id}
                    onChange={(e) => setFormData({
                      ...formData,
                      payment_details: { ...formData.payment_details, upi_id: e.target.value }
                    })}
                    placeholder="distributor@paytm"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.625rem',
                      border: `1.5px solid ${formErrors.upi_id ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = formErrors.upi_id ? '#ef4444' : '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  {formErrors.upi_id && (
                    <p style={{ color: '#ef4444', fontSize: '0.7rem', marginTop: '0.2rem', margin: 0, lineHeight: '1.2' }}>
                      {formErrors.upi_id}
                    </p>
                  )}
                </div>
              )}

              {/* Transaction ID (if payment mode is UPI) */}
              {formData.payment_mode === 'upi' && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.375rem',
                    lineHeight: '1.2'
                  }}>
                    Transaction ID <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.payment_details.transaction_id}
                    onChange={(e) => setFormData({
                      ...formData,
                      payment_details: { ...formData.payment_details, transaction_id: e.target.value }
                    })}
                    placeholder="UPI123456"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.625rem',
                      border: `1.5px solid ${formErrors.transaction_id ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = formErrors.transaction_id ? '#ef4444' : '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  {formErrors.transaction_id && (
                    <p style={{ color: '#ef4444', fontSize: '0.7rem', marginTop: '0.2rem', margin: 0, lineHeight: '1.2' }}>
                      {formErrors.transaction_id}
                    </p>
                  )}
                </div>
              )}

              {/* Notes */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.375rem',
                  lineHeight: '1.2'
                }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Partial payment"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.625rem',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    resize: 'none',
                    fontFamily: 'inherit',
                    lineHeight: '1.4'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Error Message */}
              {settlementError && (
                <div style={{
                  padding: '0.5rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '0.75rem',
                  lineHeight: '1.3'
                }}>
                  {settlementError}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  onClick={closeSettlementModal}
                  style={{
                    flex: 1,
                    padding: '0.625rem',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSettle}
                  disabled={settlingPurchaseId !== null}
                  style={{
                    flex: 1,
                    padding: '0.625rem',
                    background: settlingPurchaseId !== null
                      ? '#9ca3af'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: settlingPurchaseId !== null ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem'
                  }}
                  onMouseEnter={(e) => {
                    if (settlingPurchaseId === null) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (settlingPurchaseId === null) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {settlingPurchaseId !== null ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }}></div>
                      Processing...
                    </>
                  ) : (
                    'Settle Payment'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receive Confirmation Popup */}
      {showReceiveConfirmation && purchaseToReceive && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: isMobile ? '1rem' : '0'
        }}
        onClick={() => {
          if (!receivingPurchaseId) {
            setShowReceiveConfirmation(false);
            setPurchaseToReceive(null);
            setReceiveError('');
          }
        }}
        >
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: isMobile ? '1.25rem' : '1.5rem',
            width: '100%',
            maxWidth: isMobile ? '90%' : '420px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                if (!receivingPurchaseId) {
                  setShowReceiveConfirmation(false);
                  setPurchaseToReceive(null);
                  setReceiveError('');
                }
              }}
              disabled={receivingPurchaseId !== null}
              style={{
                position: 'absolute',
                top: '0.75rem',
                right: '0.75rem',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                color: '#6b7280',
                cursor: receivingPurchaseId !== null ? 'not-allowed' : 'pointer',
                padding: '0.25rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: receivingPurchaseId !== null ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (receivingPurchaseId === null) {
                  e.currentTarget.style.color = '#ef4444';
                }
              }}
              onMouseLeave={(e) => {
                if (receivingPurchaseId === null) {
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
            >
              ×
            </button>

            {/* Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem auto'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '0.5rem',
              textAlign: 'center',
              lineHeight: '1.2'
            }}>
              Payment Fully Settled!
            </h2>

            {/* Message */}
            <p style={{
              fontSize: '0.9rem',
              color: '#6b7280',
              textAlign: 'center',
              marginBottom: '1rem',
              lineHeight: '1.5'
            }}>
              The payment for this purchase has been fully settled.
              {purchaseToReceive.distributor_name && (
                <>
                  <br />
                  <strong>{purchaseToReceive.distributor_name}</strong>
                </>
              )}
              {purchaseToReceive.invoice_no && (
                <>
                  <br />
                  Invoice: {purchaseToReceive.invoice_no}
                </>
              )}
            </p>

            <p style={{
              fontSize: '0.95rem',
              color: '#374151',
              textAlign: 'center',
              marginBottom: '1.5rem',
              fontWeight: '600',
              lineHeight: '1.5'
            }}>
              Have you received this purchase?
            </p>

            {/* Error Message */}
            {receiveError && (
              <div style={{
                padding: '0.75rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '0.85rem',
                marginBottom: '1rem',
                lineHeight: '1.4'
              }}>
                {receiveError}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem'
            }}>
              <button
                onClick={() => {
                  setShowReceiveConfirmation(false);
                  setPurchaseToReceive(null);
                  setReceiveError('');
                }}
                disabled={receivingPurchaseId !== null}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: receivingPurchaseId !== null ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: receivingPurchaseId !== null ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (receivingPurchaseId === null) {
                    e.currentTarget.style.background = '#e5e7eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (receivingPurchaseId === null) {
                    e.currentTarget.style.background = '#f3f4f6';
                  }
                }}
              >
                Not Yet
              </button>
              <button
                onClick={handleMarkAsReceived}
                disabled={receivingPurchaseId !== null}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: receivingPurchaseId !== null
                    ? '#9ca3af'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: receivingPurchaseId !== null ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (receivingPurchaseId === null) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (receivingPurchaseId === null) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {receivingPurchaseId !== null ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Yes, Received
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '1rem' : '1.5rem',
          right: isMobile ? '1rem' : '1.5rem',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: isMobile ? '0.75rem 1rem' : '0.875rem 1.25rem',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          minWidth: isMobile ? '280px' : '320px',
          maxWidth: isMobile ? 'calc(100% - 2rem)' : '400px',
          animation: 'slideInRight 0.3s ease-out',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: isMobile ? '0.875rem' : '0.9rem',
              fontWeight: '600',
              marginBottom: '0.125rem',
              lineHeight: '1.2'
            }}>
              Payment Settled Successfully!
            </div>
            <div style={{
              fontSize: '0.75rem',
              opacity: 0.9,
              lineHeight: '1.3'
            }}>
              Settlement amount has been recorded.
            </div>
          </div>
          <button
            onClick={() => setShowSuccessNotification(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Custom Scrollbar Styles */
        .unsettled-amount-list::-webkit-scrollbar {
          width: 8px;
        }

        .unsettled-amount-list::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        .unsettled-amount-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
          transition: background 0.3s ease;
        }

        .unsettled-amount-list::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Firefox scrollbar */
        .unsettled-amount-list {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }

        /* Success Notification Animation */
        @keyframes slideInRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default UnsettledAmount;

